import React, { useEffect, useRef, useState } from 'react';
import { Button, Tabs, Modal, TabsProps, message } from 'antd';
import { DocumentsTab } from './LeadModalTabs/DocumentsTab';
import { GuarantorsTab } from './LeadModalTabs/GuarantorsTab';
import LeadInfoTab from './LeadModalTabs/LeadInfoTab';
import { ProductDetailsTab } from './LeadModalTabs/ProductDetailsTab';
import { useMutation } from '@tanstack/react-query';
import {
    checkLeadGurantorsDataForErrors,
    checkLeadInforDataForErrors,
    checkLeadProductsDataForErrors,
    formateDbDocuments,
    formateDbGurantors,
    formateDbProducts,
    getAllCombinedData,
    getUpdatedAllCombinedData,
    IDocument,
    IGuarantor,
    ILeadEntity,
    ILeadValues,
    IProduct,
} from '@rasayi-workspace/shared';
import { addLead, updateLead } from 'src/app/services/lead';
import { useExistingLead } from 'src/app/hooks/useGetExistingLead';
import { useLeadContext } from '@contexts';
import _ from 'lodash';

interface FilterBarProps {
    leadId?: string;
    leadModalVisible: boolean;
    setLeadModalVisible: (value: boolean) => void;
    refetch: () => void;
    setLeadId?: (value: string) => void;
}

export const AddLeadButton: React.FC<FilterBarProps> = ({ refetch, leadId = '', setLeadModalVisible, leadModalVisible, setLeadId }) => {
    const { fetchExistingLead, isError: leadError } = useExistingLead(leadId, true);
    const { agents } = useLeadContext();
    const { setUpdatingLeadEntity, updatingLeadEntity } = useLeadContext();
    const resetDocuments = useRef(false);

    const [messageApi, contextHolder] = message.useMessage();
    const [error, setError] = useState<{ type: 'error' | 'success' | 'info' | 'warning' | ''; message: string }>({ type: '', message: '' });
    const [activeTab, setActiveTab] = useState<string>('1');



    const [leadInfo, setLeadInfo] = useState<ILeadValues>({
        user_id: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        secondary_phone: '',
        cnic_number: '',
        dob: '',
        gender: '',
        lead_status: '',
        province: '',
        city: '',
        address: '',
        secondary_address: '',
        organization: '',
        segment: '',
        channel_id: '',
        comments: '',
    });
    const [leadDocuments, setLeadDocuments] = useState<IDocument>();
    const [leadProducts, setLeadProducts] = useState<IProduct[]>([]);
    const [leadGurantor, setLeadGurantor] = useState<IGuarantor[]>([]);

    const resetForm = () => {
        setLeadInfo({
            user_id: '',
            first_name: '',
            last_name: '',
            email: '',
            phone: '',
            secondary_phone: '',
            cnic_number: '',
            dob: '',
            gender: '',
            lead_status: '',
            province: '',
            city: '',
            address: '',
            secondary_address: '',
            organization: '',
            segment: '',
            channel_id: '',
            comments: '',
        });
        setLeadDocuments({
            profile_pic: [],
            cnic: [],
            bank_statment: [],
            bills: [],
            salary_slip: [],
            cheque: [],
            other: [],
        });
        setLeadGurantor([]);
        setLeadProducts([]);
    };

    const { mutate: createLead, isLoading } = useMutation<any>({
        mutationKey: ['createLead'],
        mutationFn: async () => {
            const formData = getAllCombinedData(leadInfo, leadProducts, leadGurantor, leadDocuments);
            return addLead(formData);
        },
        onSuccess: () => {
            onCloseHandler();
            refetch();
            handleTabChange('1');
            resetForm();
            messageApi.success('Lead created successfully!');
        },
        onError: (e: any) => {
            messageApi.error(
                <div>
                    {e?.response?.data?.message?.map((msg: string, index: number) => (
                        <div className="text-start	" key={index}>
                            {msg}
                        </div>
                    )) || 'Failed to create lead. Contact support.'}
                </div>
            );
        },
    });

    const { mutate: updateLeadHandler, isLoading: isUpdateLoading } = useMutation<any>({
        mutationKey: ['updateLead'],
        mutationFn: async () => {
            const formData = getUpdatedAllCombinedData(updatingLeadEntity, leadInfo, leadProducts, leadGurantor, leadDocuments);
            return updateLead(formData, leadId);
        },
        onSuccess: () => {
            onCloseHandler();
            refetch();
            setLeadModalVisible(false);
            resetForm();
            handleTabChange('1');
            messageApi.success('Lead updated successfully!');
        },
        onError: (e: any) => {
            messageApi.error(
                <div>
                    {e?.response?.data?.message?.map((msg: string, index: number) => (
                        <div className="text-start	" key={index}>
                            {msg}
                        </div>
                    )) || 'Failed to update lead. Contact support.'}
                </div>
            );
        },
    });

    const setLeaddata = (item: ILeadEntity) => {
        setUpdatingLeadEntity(item);
        setLeadInfo({
            user_id: item.user_id,
            first_name: item.customer?.first_name || item.first_name,
            last_name: item.customer?.last_name || item.last_name,
            email: item.customer?.email || item.email,
            phone: item.customer?.primary_mobile || item.phone,
            secondary_phone: item.secondary_phone,
            cnic_number: item.customer?.cnic_number || item.cnic_number,
            dob: item.customer?.dob || item.dob,
            gender: item.customer?.gender || item.gender,
            lead_status: item.lead_status,
            province: item.customer?.province || item.province,
            city: item.customer?.city || item.city,
            address: item.customer?.address_line1 || item.address,
            secondary_address: item.secondary_address,
            organization: item.organization,
            segment: item.segment,
            channel_id: item.channel_id,
            comments: '',
        });
        const products: IProduct[] = formateDbProducts(item.products);
        setLeadProducts(products);

        const documents: IDocument = formateDbDocuments(item.customer?.documents || []);

        setLeadDocuments(documents);

        const gurantors: IGuarantor[] = formateDbGurantors(item.guarantors);
        setLeadGurantor(gurantors);
    };

    useEffect(() => {
        if (leadId) {
            fetchExistingLead();
        }
    }, [leadId]);

    useEffect(() => {
        if (updatingLeadEntity) {
            setLeaddata(updatingLeadEntity);
        }
    }, [updatingLeadEntity]);

    useEffect(() => {
        if (leadError) {
            refetch();
            setLeadModalVisible(false);
            if (setLeadId) setLeadId('');
            resetForm();
            messageApi.error('Failed to get lead');
        }
    }, [leadError]);

    const submitData = () => {
        let dataError = '';
        if (activeTab === '1') dataError = checkLeadInforDataForErrors(leadInfo);
        if (activeTab === '2') dataError = checkLeadProductsDataForErrors(leadProducts);
        if (activeTab === '4') dataError = checkLeadGurantorsDataForErrors(leadGurantor);

        setError({ type: dataError ? 'error' : '', message: dataError });

        if (!dataError) {
            if (leadId) {
                !isUpdateLoading && updateLeadHandler();
            } else !isLoading && createLead();
        }
    };

    const onCloseHandler = () => {
        resetDocuments.current = true;
        setUpdatingLeadEntity(null);
        if (setLeadId) setLeadId('');
        setLeadModalVisible(false);
        resetForm();
        handleTabChange('1');
    };

    const handleTabChange = (key: string) => {
        let dataError = '';
        if (activeTab === '1') dataError = checkLeadInforDataForErrors(leadInfo);
        if (activeTab === '2') dataError = checkLeadProductsDataForErrors(leadProducts);
        if (activeTab === '4') dataError = checkLeadGurantorsDataForErrors(leadGurantor);

        if (!dataError) setActiveTab(key);

        setError({ type: dataError ? 'error' : '', message: dataError });
    };

    const tabItems: TabsProps['items'] = [
        {
            key: '1',
            label: 'Lead Info',
            children: (
                <LeadInfoTab
                    onSubmit={submitData}
                    saveData={setLeadInfo}
                    initialValue={leadInfo}
                    onClose={onCloseHandler}
                    isLoading={isLoading || isUpdateLoading}
                    error={error}
                    setError={setError}
                    setLeadDocuments={setLeadDocuments}
                />
            ),
        },
        {
            key: '2',
            label: 'Product Details',
            children: (
                <ProductDetailsTab
                    onClose={onCloseHandler}
                    onSaveProductValues={submitData}
                    saveData={setLeadProducts}
                    initialValue={leadProducts}
                    isLoading={isLoading || isUpdateLoading}
                    exsistingProductLength={updatingLeadEntity?.products?.length ?? 0}
                    error={error}
                    setError={setError}
                />
            ),
        },
        {
            key: '3',
            label: 'Documents',
            children: (
                <DocumentsTab
                    onClose={onCloseHandler}
                    onSaveDocumenst={submitData}
                    saveData={setLeadDocuments}
                    initialValue={leadDocuments}
                    isLoading={isLoading || isUpdateLoading}
                    leadId={leadId}
                    resetDocuments={resetDocuments}
                />
            ),
        },
        {
            key: '4',
            label: 'Guarantor',
            children: (
                <GuarantorsTab
                    onClose={onCloseHandler}
                    onSaveGuarantor={submitData}
                    saveData={setLeadGurantor}
                    initialValue={leadGurantor}
                    isLoading={isLoading || isUpdateLoading}
                    exsistingProductLength={updatingLeadEntity?.guarantors?.length ?? 0}
                    error={error}
                    setError={setError}
                    leadId={leadId}
                />
            ),
        },
    ];

    const handleAddLeadClick = () => {
        setLeadModalVisible(true);
    };

    return (
        <div>
            {contextHolder}
            <Button
                className="bg-[#BD273B] text-white hover:!text-white hover:!border-[#BD273B] px-5 rounded-lg w-[115px] h-[40px]"
                onClick={handleAddLeadClick} // Show Lead Modal
            >
                Add Lead
            </Button>

            <Modal title="Lead Details" open={leadModalVisible} onCancel={onCloseHandler} footer={null} className="rounded-lg"
                   width={1171}
                   destroyOnClose
                   centered
                   style={{ maxHeight: '774px', overflow: 'hidden' }}
            >
                <Tabs
                    destroyInactiveTabPane={true}
                    activeKey={activeTab}
                    defaultActiveKey="1"
                    items={tabItems}
                    onChange={handleTabChange}
                    className="custom-tabs"
                    renderTabBar={(props, DefaultTabBar) => (
                        <DefaultTabBar
                            {...props}
                            style={{
                                borderBottom: '1px solid #DBD7D7',
                            }}
                        />
                    )}
                ></Tabs>
            </Modal>
        </div>
    );
};

